import { type ClientMealsPlanDetails } from "@fitness-app/data-models/entities/ClientNutrition";
import { type IngredientWithPortion } from "@fitness-app/data-models/entities/Ingredient";
import { type DishInMeal, type MealsPlanDetails } from "@fitness-app/data-models/entities/MealsPlan";
import { calculateMeasureMultiplier } from "@fitness-app/utils/src/nutrition/calculateMeasureMultiplier";
import { recalculateMeasures } from "@fitness-app/utils/src/nutrition/recalculateMeasures";
import {
  reduceDishIngredientsNutrients,
  reduceMealsNutrients,
} from "@fitness-app/utils/src/nutrition/reduceMealsNutrients";
import { getNutrientsInPortion } from "@fitness-app/utils/src/nutrition/sumNutrients";

import { mealTranslation, weekdaysTranslation } from "./TraineePdfGenerator/components/constants";
import { measuresMap } from "./TraineePdfGenerator/components/measure-maps";

interface PlanInfo {
  name: string;
  targetCalories?: number | null;
  targets?: {
    protein: number;
    carbs: number;
    fat: number;
    macroUnit?: "percentage" | "grams" | null;
  } | null;
}

const renderIngredientProduct = (product: IngredientWithPortion): string[] => {
  const macros = reduceDishIngredientsNutrients([product]);
  const brand = product.brand ? ` (${product.brand})` : "";
  const calories = getNutrientsInPortion(product, "calories", 0);

  return [
    `#### ${product.name}${brand} — ${calories} kcal — ${product.portion || 0} g`,
    "",
    `Białko: ${macros.protein || 0} g | Węglowodany: ${macros.carbs || 0} g | Tłuszcze: ${macros.fat || 0} g`,
  ];
};

const renderDish = (dish: DishInMeal): string[] => {
  const portionsInfo =
    dish.portions && dish.portions > 1 ? ` (${dish.portionCount || 1} z ${dish.portions} porcji)` : "";
  const prepTime = dish.prepTime ? ` — ${dish.prepTime} min` : "";
  const lines = [`#### ${dish.name} — ${dish.calories} kcal${prepTime}${portionsInfo}`];

  const ingredients = dish.ingredients ?? [];
  if (ingredients.length > 0) {
    lines.push("", "Składniki:");
    for (const ingredient of ingredients) {
      const enhanced = recalculateMeasures(ingredient);
      const { multiplier } = calculateMeasureMultiplier(enhanced, measuresMap);
      // gram/ml pseudo-measures produce noise like "400 g (4 g)" — the grams are already there
      const isRedundantMeasure = !enhanced.measure || ["g", "ml"].includes(enhanced.measure.trim().toLowerCase());
      const measureText = isRedundantMeasure ? "" : ` (${multiplier || 1} ${enhanced.measure})`;
      lines.push(
        `- ${enhanced.name || "-"} — ${enhanced.portion || 0} g${measureText} — ${enhanced.portionCalories || 0} kcal`,
      );
    }
  }

  const steps = dish.preparationSteps ?? [];
  if (steps.length > 0) {
    lines.push("", "Sposób przygotowania:");
    steps.forEach((step, index) => lines.push(`${index + 1}. ${step}`));
  }

  return lines;
};

export const generateNutritionPlanMarkdown = (
  plan: PlanInfo,
  planDetails: Pick<ClientMealsPlanDetails | MealsPlanDetails, "weeks">,
): string => {
  const lines: string[] = [`# ${plan.name}`];

  if (plan.targetCalories) {
    const unit = plan.targets?.macroUnit === "grams" ? " g" : "%";
    const targetsText = plan.targets
      ? ` | B: ${plan.targets.protein}${unit} / W: ${plan.targets.carbs}${unit} / T: ${plan.targets.fat}${unit}`
      : "";
    lines.push("", `Cel dzienny: ${plan.targetCalories} kcal${targetsText}`);
  }

  const weeks = planDetails.weeks ?? [];

  weeks.forEach((week, weekIndex) => {
    if (weeks.length > 1) {
      lines.push("", `## Tydzień ${week.weekNumber ?? weekIndex + 1}`);
    }

    for (const day of week.days ?? []) {
      const dayName = weekdaysTranslation[day.name as keyof typeof weekdaysTranslation] ?? day.name;
      lines.push("", `## ${dayName}`);
      if (day.comment) {
        lines.push("", `> ${day.comment}`);
      }

      for (const meal of day.meals ?? []) {
        const { protein, carbs, fat, calories } = reduceMealsNutrients([meal]);
        const mealName = (mealTranslation as Record<string, string>)[meal.type] ?? meal.name ?? meal.type;
        lines.push("", `### ${mealName} — ${calories} kcal | B: ${protein} g | W: ${carbs} g | T: ${fat} g`);
        if (meal.comment) {
          lines.push("", `> ${meal.comment}`);
        }

        for (const dishOrProduct of meal.dishes ?? []) {
          lines.push(
            "",
            ...(dishOrProduct.type === "ingredient"
              ? renderIngredientProduct(dishOrProduct)
              : renderDish(dishOrProduct)),
          );
        }
      }
    }
  });

  return `${lines.join("\n")}\n`;
};

export const downloadNutritionPlanMarkdown = (
  plan: PlanInfo,
  planDetails: Pick<ClientMealsPlanDetails | MealsPlanDetails, "weeks">,
): void => {
  const markdown = generateNutritionPlanMarkdown(plan, planDetails);
  const fileName = `${(plan.name || "dieta").replace(/[\\/:*?"<>|]/g, "-")}.md`;
  const blob = new Blob([markdown], { type: "text/markdown;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};
